import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ReportUpload from '../components/ReportUpload'
import { PatientCreationForm } from '../components/PatientCreationForm'
import { useAuth } from '../contexts/AuthContext'
import patientService from '../services/patientService'
import { dashboardService, DashboardStats } from '../services/dashboardService'
import { appointmentAPI, healthAPI } from '../services/api'
import toast from 'react-hot-toast'
import { 
  FiPlus, 
  FiSearch, 
  FiUsers, 
  FiFileText, 
  FiCalendar, 
  FiTrendingUp,
  FiUser,
  FiPhone,
  FiMail,
  FiEdit,
  FiTrash2,
  FiEye,
  FiPackage,
  FiClock,
  FiMessageCircle
} from 'react-icons/fi'

interface Patient {
  id: string
  name: string
  cnic: string
  phone: string
  email: string
  lastVisit: string
  status: 'active' | 'pending' | 'inactive'
  medications: number
  appointments: number
}

// Real patients will be loaded from the database

export const DoctorDashboard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [showAddPatient, setShowAddPatient] = useState(false)
  const [showUploadReport, setShowUploadReport] = useState(false)
  const [patients, setPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(true)
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalPatients: 0,
    activePatients: 0,
    pendingReports: 0,
    todayAppointments: 0
  })
  const [loadingStats, setLoadingStats] = useState(true)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [showCredentials, setShowCredentials] = useState(false)
  const [patientCredentials, setPatientCredentials] = useState<any>(null)
  const [showScheduleAppointment, setShowScheduleAppointment] = useState(false)
  const [showHealthRecords, setShowHealthRecords] = useState(false)
  const [healthRecords, setHealthRecords] = useState<any[]>([])
  const [loadingRecords, setLoadingRecords] = useState(false)
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    date: '',
    time: '',
    notes: '',
    providerName: '',
    providerType: 'doctor'
  })
  
  const { user, isLoading: authLoading, token } = useAuth()
  const navigate = useNavigate()

  // Debug authentication state
  useEffect(() => {
    console.log('DoctorDashboard auth state:', {
      user: user ? { id: user.id, email: user.email, role: user.role } : null,
      authLoading,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token',
      localStorageToken: localStorage.getItem('token') ? `${localStorage.getItem('token')?.substring(0, 20)}...` : 'No token'
    })
  }, [user, authLoading, token])

  // Load patients and dashboard stats from database only when user is authenticated
  useEffect(() => {
    if (user && !authLoading) {
      loadPatients()
      loadDashboardStats()
    }
  }, [user, authLoading])

  const loadPatients = async () => {
    try {
      setLoading(true)
      const patientsData = await patientService.getPatients()
      // Ensure patientsData is always an array
      const patientsArray = Array.isArray(patientsData) ? patientsData : []
      console.log('Loaded patients:', patientsArray)
      setPatients(patientsArray)
    } catch (error) {
      console.error('Error loading patients:', error)
      toast.error('Failed to load patients')
      // Set empty array on error to prevent crashes
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  const loadDashboardStats = async () => {
    if (!user?.id) return
    
    try {
      setLoadingStats(true)
      console.log('Loading dashboard stats for doctor:', {
        userId: user.id,
        userRole: user.role,
        userEmail: user.email
      })
      const stats = await dashboardService.getDashboardStats(user.id)
      console.log('Loaded dashboard stats:', stats)
      setDashboardStats(stats)
    } catch (error) {
      console.error('Error loading dashboard stats:', error)
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      })
      toast.error('Failed to load dashboard statistics')
      // Set default stats on error
      setDashboardStats({
        totalPatients: 0,
        activePatients: 0,
        pendingReports: 0,
        todayAppointments: 0
      })
    } finally {
      setLoadingStats(false)
    }
  }

  const handleShowCredentials = async (patientId: string) => {
    try {
      const response = await patientService.getPatientCredentials(patientId)
      setPatientCredentials(response)
      setShowCredentials(true)
    } catch (error) {
      console.error('Error fetching credentials:', error)
      toast.error('Failed to fetch patient credentials')
    }
  }

  const handleResetPassword = async (patientId: string) => {
    try {
      const response = await patientService.resetPatientPassword(patientId)
      setPatientCredentials(response)
      toast.success('Password reset successfully!')
    } catch (error) {
      console.error('Error resetting password:', error)
      toast.error('Failed to reset password')
    }
  }


  const handleScheduleAppointment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedPatient) return
    try {
      const dateTime = `${newAppointment.date}T${newAppointment.time}:00`
      await appointmentAPI.createAppointment({
        title: newAppointment.title,
        date: dateTime,
        provider: {
          name: newAppointment.providerName || user?.firstName + ' ' + user?.lastName || 'Dr.',
          type: newAppointment.providerType as any
        },
        notes: newAppointment.notes,
        userId: selectedPatient.id
      })
      toast.success('Appointment scheduled successfully!')
      setShowScheduleAppointment(false)
      setNewAppointment({ title: '', date: '', time: '', notes: '', providerName: '', providerType: 'doctor' })
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to schedule appointment')
    }
  }

  const handleViewHealthRecords = async () => {
    if (!selectedPatient) return
    try {
      setShowHealthRecords(true)
      setLoadingRecords(true)
      const res = await healthAPI.getRecords({ userId: selectedPatient.id })
      setHealthRecords(res.data.data.records || [])
    } catch (e: any) {
      toast.error('Failed to load health records')
    } finally {
      setLoadingRecords(false)
    }
  }

  const filteredPatients = Array.isArray(patients) ? patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.cnic?.includes(searchTerm) ||
    patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : []

  // Use real dashboard stats from Firebase
  const stats = dashboardStats

  // Show loading while authentication is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Show error if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need to be logged in to access this page.</p>
          <Link to="/doctor/login" className="btn btn-primary">
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <FiUsers className="h-8 w-8 text-primary-500" />
              <h1 className="ml-2 text-2xl font-bold text-gray-900">Doctor Portal</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  loadPatients()
                  loadDashboardStats()
                  toast.success('Dashboard refreshed!')
                }}
                className="btn btn-outline btn-md inline-flex items-center"
                disabled={loadingStats}
              >
                <FiClock className="mr-2 h-4 w-4" />
                {loadingStats ? 'Refreshing...' : 'Refresh'}
              </button>
              <Link
                to="/doctor/chat"
                className="btn btn-outline btn-md inline-flex items-center"
              >
                <FiMessageCircle className="mr-2 h-4 w-4" />
                Chat with Patients
              </Link>
              <button
                onClick={() => setShowAddPatient(true)}
                className="btn btn-primary btn-md inline-flex items-center"
              >
                <FiPlus className="mr-2 h-4 w-4" />
                Add Patient
              </button>
              <Link
                to="/"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="card-content flex items-center">
              <div className="flex items-center h-full">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <FiUsers className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Patients</p>
                  {loadingStats ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{stats.totalPatients}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center">
                <div className="p-2 bg-success-100 rounded-lg">
                  <FiTrendingUp className="h-6 w-6 text-success-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Patients</p>
                  {loadingStats ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{stats.activePatients}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center">
                <div className="p-2 bg-warning-100 rounded-lg">
                  <FiFileText className="h-6 w-6 text-warning-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending Follow-ups</p>
                  {loadingStats ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingReports}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <FiCalendar className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Today's Follow-ups</p>
                  {loadingStats ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{stats.todayAppointments}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Patient List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Patient List */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center">
                  <h3 className="card-title">Patients</h3>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search patients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-10 w-64"
                    />
                  </div>
                </div>
              </div>
              <div className="card-content">
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading patients...</div>
                ) : filteredPatients.length === 0 ? (
                  <div className="text-center py-8">
                    <FiUsers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No patients yet</h3>
                    <p className="text-gray-600 mb-4">Start by adding your first patient</p>
                    <button
                      onClick={() => setShowAddPatient(true)}
                      className="btn btn-primary  py-2 px-4"
                    >
                      <FiPlus className="mr-2 h-4 w-4" />
                      Add First Patient
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredPatients.map((patient) => (
                    <div
                      key={patient.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedPatient?.id === patient.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedPatient(patient)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-primary-100 rounded-full">
                              <FiUser className="h-4 w-4 text-primary-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{patient.firstName} {patient.lastName}</h4>
                              <p className="text-sm text-gray-600">{patient.cnic || 'No CNIC'}</p>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <FiPhone className="h-4 w-4 mr-1" />
                              {patient.phoneNumber || 'No phone'}
                            </div>
                            <div className="flex items-center">
                              <FiMail className="h-4 w-4 mr-1" />
                              {patient.email}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-sm">
                            <span className={`badge ${
                              patient.status === 'active' ? 'badge-success' : 
                              patient.status === 'pending' ? 'badge-warning' : 'badge-secondary'
                            }`}>
                              {patient.status}
                            </span>
                            <span className="text-gray-500">Last visit: {patient.lastVisit}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <FiEdit className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Patient Details */}
          <div className="lg:col-span-1">
            {selectedPatient ? (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Patient Details</h3>
                </div>
                <div className="card-content">
                  <div className="space-y-6">
                    {/* Patient Info */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Personal Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">{selectedPatient.firstName} {selectedPatient.lastName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">CNIC:</span>
                          <span className="font-medium">{selectedPatient.cnic || 'No CNIC'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Phone:</span>
                          <span className="font-medium">{selectedPatient.phoneNumber || 'No phone'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">{selectedPatient.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className={`badge ${
                            selectedPatient.status === 'active' ? 'badge-success' : 
                            selectedPatient.status === 'pending' ? 'badge-warning' : 'badge-secondary'
                          }`}>
                            {selectedPatient.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                      <div className="space-y-2">
                        <button 
                          onClick={() => navigate('/prescription', { state: { patient: selectedPatient } })}
                          className="btn btn-outline btn-sm w-full justify-start"
                        >
                          <FiPackage className="mr-2 h-4 w-4" />
                          Manage Medications
                        </button>
                        <button 
                          onClick={() => setShowUploadReport(true)}
                          className="btn btn-outline btn-sm w-full justify-start"
                        >
                          <FiFileText className="mr-2 h-4 w-4" />
                          Upload Report
                        </button>
                        <button onClick={() => setShowScheduleAppointment(true)} className="btn btn-outline btn-sm w-full justify-start">
                          <FiCalendar className="mr-2 h-4 w-4" />
                          Schedule Appointment
                        </button>
                        <button onClick={handleViewHealthRecords} className="btn btn-outline btn-sm w-full justify-start">
                          <FiEye className="mr-2 h-4 w-4" />
                          View Health Records
                        </button>
                        <button 
                          onClick={() => handleShowCredentials(selectedPatient.id)}
                          className="btn btn-outline btn-sm w-full justify-start"
                        >
                          <FiEdit className="mr-2 h-4 w-4" />
                          View Credentials
                        </button>
                        <Link to="/doctor/chat" className="btn btn-outline btn-sm w-full justify-start">
                          <FiMessageCircle className="mr-2 h-4 w-4" />
                          Chat with Patients
                        </Link>
                      </div>
                    </div>

                    {/* Health Summary */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Health Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Active Medications:</span>
                          <span className="font-medium">{selectedPatient.medications}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Upcoming Appointments:</span>
                          <span className="font-medium">{selectedPatient.appointments}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Visit:</span>
                          <span className="font-medium">{selectedPatient.lastVisit}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-content text-center py-12">
                  <FiUser className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Patient</h3>
                  <p className="text-gray-600">Choose a patient from the list to view their details and manage their care.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Patient Modal */}
      {showAddPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <PatientCreationForm
            onPatientCreated={(patient, credentials) => {
              setPatientCredentials(credentials)
              setShowCredentials(true)
              setShowAddPatient(false)
              loadPatients()
              loadDashboardStats() // Refresh dashboard stats
            }}
            onClose={() => setShowAddPatient(false)}
          />
        </div>
      )}

      {/* Upload Report Modal */}
      {showUploadReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Medical Report</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient
                </label>
                <select className="input w-full">
                  <option value="">Select a patient</option>
                  {Array.isArray(patients) && patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.firstName} {patient.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Type
                </label>
                <select className="input w-full">
                  <option value="">Select report type</option>
                  <option value="lab">Lab Report</option>
                  <option value="imaging">Imaging Report</option>
                  <option value="pathology">Pathology Report</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report File
                </label>
                <ReportUpload
                  onUpload={setUploadedFile}
                  onRemove={() => setUploadedFile(null)}
                  uploadedFile={uploadedFile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  className="input w-full h-20"
                  placeholder="Add any additional notes about this report..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowUploadReport(false)
                  setUploadedFile(null)
                }}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // Handle report upload
                  console.log('Uploading report...')
                  setShowUploadReport(false)
                  setUploadedFile(null)
                }}
                disabled={!uploadedFile}
                className="btn btn-primary   py-2 px-4 disabled:opacity-50"
              >
                Upload Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Appointment Modal */}
      {showScheduleAppointment && selectedPatient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Schedule Appointment for {selectedPatient.firstName} {selectedPatient.lastName}</h3>
            <form onSubmit={handleScheduleAppointment} className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Appointment Title</label>
                <input required className="input w-full" value={newAppointment.title} onChange={e => setNewAppointment({...newAppointment, title: e.target.value})} placeholder="Follow-up consultation" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">Date</label>
                  <input required type="date" className="input w-full" value={newAppointment.date} onChange={e => setNewAppointment({...newAppointment, date: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm mb-1">Time</label>
                  <input required type="time" className="input w-full" value={newAppointment.time} onChange={e => setNewAppointment({...newAppointment, time: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">Provider Name</label>
                <input className="input w-full" value={newAppointment.providerName} onChange={e => setNewAppointment({...newAppointment, providerName: e.target.value})} placeholder={user?.firstName + ' ' + user?.lastName || 'Dr.'} />
              </div>
              <div>
                <label className="block text-sm mb-1">Notes</label>
                <textarea className="input w-full" rows={3} value={newAppointment.notes} onChange={e => setNewAppointment({...newAppointment, notes: e.target.value})} placeholder="Additional notes..." />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" className="btn btn-outline" onClick={() => setShowScheduleAppointment(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Health Records Modal */}
      {showHealthRecords && selectedPatient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Health Records for {selectedPatient.firstName} {selectedPatient.lastName}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setShowHealthRecords(false)}>Close</button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {loadingRecords ? (
                <div className="text-center py-8 text-gray-500">Loading records...</div>
              ) : healthRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No health records found.</div>
              ) : (
                <div className="space-y-3">
                  {healthRecords.map((record: any) => (
                    <div key={record.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{record.title || 'Untitled'}</div>
                          <div className="text-sm text-gray-600">{record.recordType || 'general'}</div>
                          {record.description && <div className="text-sm text-gray-500 mt-1">{record.description}</div>}
                          <div className="text-xs text-gray-400 mt-1">{new Date(record.date).toLocaleDateString()}</div>
                        </div>
                        <span className={`badge ${record.recordType === 'appointment' ? 'badge-primary' : record.recordType === 'lab' ? 'badge-warning' : 'badge-secondary'}`}>
                          {record.recordType}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Patient Credentials Modal */}
      {showCredentials && patientCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Patient Credentials</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Login Information</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{patientCredentials.firstName} {patientCredentials.lastName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-medium">{patientCredentials.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">CNIC:</span>
                    <span className="ml-2 font-medium">{patientCredentials.cnic}</span>
                  </div>
                  {patientCredentials.generatedPassword && (
                    <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                      <span className="text-gray-600">Generated Password:</span>
                      <div className="mt-1 font-mono text-lg font-bold text-yellow-800 bg-yellow-100 p-2 rounded">
                        {patientCredentials.generatedPassword}
                      </div>
                      <p className="text-xs text-yellow-700 mt-1">
                        Please share this password with the patient securely
                      </p>
                    </div>
                  )}
                  {patientCredentials.newPassword && (
                    <div className="bg-green-50 p-3 rounded border border-green-200">
                      <span className="text-gray-600">New Password:</span>
                      <div className="mt-1 font-mono text-lg font-bold text-green-800 bg-green-100 p-2 rounded">
                        {patientCredentials.newPassword}
                      </div>
                      <p className="text-xs text-green-700 mt-1">
                        Password has been reset successfully
                      </p>
                    </div>
                  )}
                  <div className="mt-3">
                    <span className="text-gray-600">Login URL:</span>
                    <div className="mt-1 text-blue-600 break-all">
                      {patientCredentials.loginUrl || 'http://localhost:3000/patient/login'}
                    </div>
                  </div>
                </div>
              </div>

              {patientCredentials.message && (
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-sm text-blue-800">{patientCredentials.message}</p>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCredentials(false)
                  setPatientCredentials(null)
                }}
                className="btn btn-outline"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleResetPassword(patientCredentials.id)}
                className="btn btn-primary   py-2 px-4"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
